import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import React, { useEffect, useRef, useState } from "react";
import useAdd from "../../../../api/useAdd";
import useUpdate from "../../../../api/useUpdate";
import { useAuth } from "../../../../context";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../../api/useFetchObjects";
import useDelete from "../../../../api/useDelete";
import Status from "./Status";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

const AddClient = ({ onClose, data = null }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(null);
  const auth = useAuth();
  const token = auth?.user?.token;

  // Ref to store the debounce timer
  const timerRef = useRef(null);
  const [filterValue, setFilterValue] = useState("");

  // Initialize form data.
  const [formData, setFormData] = useState({
    full_name: data?.full_name || "",
    contact: data?.contact || "",
    contact_2: data?.contact_2 || "",
    address: data?.address || "",
    procedure: data?.procedure || "",
    procedure_date: data?.procedure_date || "",
    procedure_day: data?.procedure_day || "",
    status: data?.status || "",
    age: data?.age || "",
    gender: data?.gender || "",
    price: data?.price || "",
    advance: data?.advance || "",
    description: data?.description || "",
  });

  const [formErrors, setFormErrors] = useState({});

  // Fetch statuses and procedure types as before
  const {
    data: status,
    isLoading: isLoadingStatus,
    isError: isErrorStatus,
  } = useFetchObjects(["status"], "status", token);

  const {
    data: types,
    isLoading: isLoadingType,
    isError: isErrorType,
  } = useFetchObjects(["procedure-type"], "procedure-type", token);

  // Use our custom hook to search for a patient by contact.
  const {
    data: patient,
    isLoading: isLoadingPatient,
    isError: isErrorPatient,
    refetch: refetchPatient,
  } = useFetchObjects(["patients"], `patients/?phone=${filterValue}`, token, {
    enabled: false,
  });

  const { handleAdd, loading, resetForm } = useAdd("client", token);
  const { handleUpdate, loading: loadingEdit } = useUpdate("client", token);
  const { handleDelete, ConfirmDialog } = useDelete("status", token);

  // Helper function: if contact starts with "+" return unchanged, if it starts with "0", remove it.
  const formatContact = (contact) => {
    if (contact.startsWith("+")) return contact;
    if (contact.startsWith("0")) return contact.substring(1);
    return contact;
  };

  // Generic input change handler for all fields.
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Debounce the main contact input changes.
  useEffect(() => {
    const contactTrim = formData.contact.trim();
    if (contactTrim !== "") {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        // Use formatted contact for search
        setFilterValue(formatContact(contactTrim));
      }, 300);
      return () => clearTimeout(timerRef.current);
    } else {
      setFilterValue("");
    }
  }, [formData.contact]);

  // Immediate update when pasting into the contact field.
  const handleContactPaste = (e) => {
    setTimeout(() => {
      const pastedValue = e.target.value.trim();
      if (timerRef.current) clearTimeout(timerRef.current);
      setFilterValue(formatContact(pastedValue));
    }, 500);
  };

  // Reset form data when resetForm flag is set.
  useEffect(() => {
    if (resetForm) {
      setFormData({
        full_name: "",
        contact: "",
        contact_2: "",
        address: "",
        procedure: "",
        procedure_date: "",
        procedure_day: "",
        status: "",
        age: "",
        gender: "",
        price: "",
        advance: "",
        description: "",
      });
    }
  }, [resetForm]);

  // Whenever filterValue changes and is non-empty, trigger the patient fetch.
  useEffect(() => {
    if (filterValue.trim() !== "") {
      refetchPatient();
    }
  }, [filterValue, refetchPatient]);

  const handleSubmit = (e) => {
    e.preventDefault();
    let errors = {};

    if (!formData?.full_name) {
      errors.full_name = t("call_center.fullNameRequired");
    }
    // Removed extra contact validations – now relying on native required

    if (!data && Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // Format the contacts using the same logic.
    const submitData = new FormData();
    submitData.append("full_name", formData.full_name);
    submitData.append("contact", formatContact(formData.contact.trim()));
    submitData.append("contact_2", formatContact(formData.contact_2.trim()));
    submitData.append("address", formData.address);
    submitData.append("procedure", formData.procedure);
    if (formData.procedure_date) {
      submitData.append("procedure_date", formData.procedure_date);
    }
    submitData.append("procedure_day", formData.procedure_day);
    submitData.append("status", formData.status);
    submitData.append("age", formData.age);
    submitData.append("gender", formData.gender);
    submitData.append("price", formData.price);
    submitData.append("advance", formData.advance);
    submitData.append("description", formData.description);

    if (data) {
      handleUpdate(data.id, submitData);
      onClose();
    } else {
      handleAdd(submitData);
    }
    setFormErrors({});
  };

  const handleOpenDialog = () => {
    setOpen(!open);
  };

  return (
    <Grid container justifyContent="center">
      <Grid item xs={11.5} mt={2}>
        <Paper
          sx={{ padding: "20px", borderRadius: "10px", boxShadow: 3 }}
          component="form"
          onSubmit={handleSubmit}
        >
          <Typography
            variant="h5"
            gutterBottom
            fontWeight={"bold"}
            align="center"
            sx={{ borderBottom: "3px solid #007471", pb: 1.5 }}
          >
            {data
              ? t("call_center.followClient")
              : t("call_center.addNewClient")}
          </Typography>

          {/* Display patient info only if a matching patient was found */}
          {!isLoadingPatient &&
            !isErrorPatient &&
            patient?.results &&
            patient.results.length > 0 && (
              <Paper
                sx={{
                  p: 2,
                  mb: 2,
                  backgroundColor: "#e0f7fa",
                  borderRadius: "8px",
                }}
                elevation={2}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  {t("reception.patient")}
                </Typography>
                <Typography>
                  {t("reception.firstName")}: {patient.results[0].firstname}
                </Typography>
                <Typography>
                  {t("reception.lastName")}: {patient.results[0].lastname}
                </Typography>
                <Typography>
                  {t("reception.phone")}: {patient.results[0].phone}
                </Typography>
                <Typography>
                  {t("reception.age")}: {patient.results[0].age}
                </Typography>
                <Typography>
                  {t("reception.gender")}: {patient.results[0].gender_display}
                </Typography>
                <Typography>
                  {t("reception.address")}: {patient.results[0].address}
                </Typography>
                <Typography>
                  {t("reception.bloodType")}:{" "}
                  {patient.results[0].blood_type_display}
                </Typography>
              </Paper>
            )}

          <Grid container spacing={3} mt={1}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                label={t("call_center.fullName")}
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                error={!!formErrors.full_name}
                helperText={formErrors.full_name}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                label={t("call_center.contactNumber")}
                name="contact"
                value={formData.contact}
                onChange={handleInputChange}
                onPaste={handleContactPaste}
                required
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                label={t("call_center.additionalNumber")}
                name="contact_2"
                value={formData.contact_2}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={t("call_center.address")}
                variant="outlined"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label={t("call_center.procedure")}
                variant="outlined"
                name="procedure"
                value={formData.procedure}
                onChange={handleInputChange}
              >
                <MenuItem value="">{t("call_center.clear", "Clear")}</MenuItem>
                {isLoadingType ? (
                  <MenuItem disabled>
                    <Grid container justifyContent="center">
                      <CircularProgress size={24} />
                    </Grid>
                  </MenuItem>
                ) : isErrorType ? (
                  <MenuItem disabled>
                    <Typography color="error">
                      {t("call_center.errorField")}
                    </Typography>
                  </MenuItem>
                ) : (
                  types?.map((item, i) => (
                    <MenuItem value={item.id} key={i}>
                      <Typography>{item.name}</Typography>
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="datetime-local"
                variant="outlined"
                label={t("call_center.procedureDate")}
                name="procedure_date"
                value={formData.procedure_date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="procedure_days">
                  {t("call_center.procedureDay")}
                </InputLabel>
                <Select
                  labelId="procedure_days"
                  id="procedure_day"
                  name="procedure_day"
                  value={formData.procedure_day}
                  onChange={handleInputChange}
                  label={t("call_center.procedureDay")}
                >
                  <MenuItem value="">{t("call_center.none", "None")}</MenuItem>
                  <MenuItem value="monday">{t("call_center.monday")}</MenuItem>
                  <MenuItem value="tuesday">
                    {t("call_center.tuesday")}
                  </MenuItem>
                  <MenuItem value="wednesday">
                    {t("call_center.wednesday")}
                  </MenuItem>
                  <MenuItem value="thursday">
                    {t("call_center.thursday")}
                  </MenuItem>
                  <MenuItem value="friday">{t("call_center.friday")}</MenuItem>
                  <MenuItem value="saturday">
                    {t("call_center.saturday")}
                  </MenuItem>
                  <MenuItem value="sunday">{t("call_center.sunday")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              {/* Status select with clear option */}
              <TextField
                select
                fullWidth
                variant="outlined"
                label={t("call_center.status")}
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <MenuItem value="">{t("call_center.clear", "Clear")}</MenuItem>
                {isLoadingStatus ? (
                  <MenuItem disabled>
                    <Grid container justifyContent="center">
                      <CircularProgress size={24} />
                    </Grid>
                  </MenuItem>
                ) : isErrorStatus ? (
                  <MenuItem disabled>
                    <Typography color="error">
                      {t("call_center.errorField")}
                    </Typography>
                  </MenuItem>
                ) : (
                  status?.map((item, i) => (
                    <MenuItem value={item.id} key={i}>
                      <Grid
                        container
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography>{item.name}</Typography>
                        {formData.status !== item.id && (
                          <DeleteForeverIcon
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(item.id);
                            }}
                            sx={{ color: "red" }}
                          />
                        )}
                      </Grid>
                    </MenuItem>
                  ))
                )}
              </TextField>
              <Button
                fullWidth
                variant="outlined"
                sx={{ textTransform: "capitalize", mt: 1 }}
                onClick={handleOpenDialog}
              >
                {t("call_center.addNewStatus")}
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                label={t("call_center.age")}
                name="age"
                value={formData.age}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <TextField
                select
                fullWidth
                variant="outlined"
                label={t("call_center.gender")}
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
              >
                <MenuItem value="">{t("call_center.clear", "Clear")}</MenuItem>
                <MenuItem value={1}>{t("call_center.male")}</MenuItem>
                <MenuItem value={2}>{t("call_center.female")}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                label={t("call_center.price")}
                name="price"
                value={formData.price}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                label={t("call_center.advance")}
                name="advance"
                value={formData.advance}
                onChange={handleInputChange}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                multiline
                variant="outlined"
                label={t("call_center.description")}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} textAlign="end">
              <LoadingButton
                loading={loading || loadingEdit}
                variant="contained"
                color="success"
                type="submit"
                sx={{ py: 1, px: 3 }}
              >
                {t("call_center.save")}
              </LoadingButton>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <ConfirmDialog />
      <Status open={open} onClose={handleOpenDialog} token={token} />
    </Grid>
  );
};

export default AddClient;
